// Industry × Service grid — high-intent landing pages like
//   /seo-for-hvac, /web-design-for-plumbing, /google-maps-for-roofing,
//   /ppc-for-electrical, etc.
//
// One source of truth for every {industry, service} combination.
// Each combo gets a unique on-page modifier paragraph so pages are not
// boilerplate. URLs follow the pattern: /{serviceSlug}-for-{industrySlug}
//
// Add new services by extending INDUSTRY_SERVICES.
// Add new industries by adding rich data to industriesData.ts AND
// listing the slug in PRIORITY_INDUSTRY_SLUGS below.

import {
  Search,
  Globe,
  MapPin,
  MousePointerClick,
  type LucideIcon,
} from "lucide-react";
import {
  INDUSTRY_PAGES,
  type IndustryKey,
  type IndustryPage,
} from "./industriesData";

export type IndustryServiceKey = "seo" | "web-design" | "google-maps" | "ppc";

export type IndustryServiceConfig = {
  key: IndustryServiceKey;
  /** URL slug, e.g. "seo" → /seo-for-hvac */
  slug: string;
  /** Display name */
  serviceName: string;
  /** Short verb-led label */
  verb: string; // "Rank", "Build", "Dominate", "Convert"
  /** Hub route to link back to (e.g. "/colorado-springs-seo") */
  hubPath: string;
  /** Pricing route to link to */
  pricingPath: string;
  /** Lucide icon */
  icon: LucideIcon;
  /** What the service literally does — used in intro */
  whatItIs: string;
  /** Why this service matters for that industry — generic, woven into copy */
  whyItMatters: (industryName: string, shortName: string) => string;
  /** Three pillars — what we actually do */
  pillars: { title: string; body: (industryName: string, shortName: string) => string }[];
  /** SEO meta */
  meta: {
    title: (industryName: string) => string;
    description: (industryName: string, shortName: string) => string;
  };
  /** Hero copy */
  heroEyebrow: (industryName: string) => string;
  heroTitle: (industryName: string) => { lead: string; gold: string; trail?: string };
  heroDescription: (industryName: string, shortName: string) => string;
  /** CTA label */
  ctaLabel: string;
  /** Service-specific FAQs that get merged with industry FAQs */
  serviceFaqs: { q: (industryName: string) => string; a: (industryName: string, shortName: string) => string }[];
};

/* ============================================================
   SERVICES (4) — kept in display order
============================================================ */
export const INDUSTRY_SERVICES: Record<IndustryServiceKey, IndustryServiceConfig> = {
  seo: {
    key: "seo",
    slug: "seo",
    serviceName: "SEO",
    verb: "Rank",
    hubPath: "/colorado-springs-seo",
    pricingPath: "/pricing",
    icon: Search,
    whatItIs:
      "Search engine optimization — the on-page, technical, and off-page work that gets you found on Google when local customers search for your services.",
    whyItMatters: (industry, shortName) =>
      `When someone Googles "${industry.toLowerCase()} near me" or a specific service in your category, the top 3 organic results plus the Map Pack capture 70%+ of clicks. If your ${shortName} business isn't there, you're invisible to your hottest leads.`,
    pillars: [
      {
        title: "Service-specific keyword targeting",
        body: (industry, shortName) =>
          `We map every service your ${shortName} offers to the actual keywords Colorado customers search — and build dedicated pages that rank for each one.`,
      },
      {
        title: "Local + neighborhood SEO",
        body: (industry) =>
          `Generic city-wide ${industry.toLowerCase()} keywords are crowded. We win neighborhood-level searches first, then expand outward as authority compounds.`,
      },
      {
        title: "Content + link authority",
        body: (industry, shortName) =>
          `Real content written by humans who understand what ${shortName} customers actually ask — earning links from local Colorado sites, not PBNs or junk.`,
      },
    ],
    meta: {
      title: (industry) => `${industry} SEO Services in Colorado | CWI`,
      description: (industry, shortName) =>
        `Local SEO for Colorado ${shortName} businesses. Rank for the searches that actually drive ${industry.toLowerCase()} jobs — Map Pack, organic, and content. Transparent pricing.`,
    },
    heroEyebrow: (industry) => `SEO for ${industry}`,
    heroTitle: (industry) => ({
      lead: "SEO that ranks Colorado ",
      gold: industry,
      trail: " businesses where buyers search",
    }),
    heroDescription: (industry, shortName) =>
      `We help Colorado ${shortName} companies rank in Google's Map Pack and organic results for the high-intent searches that book real ${industry.toLowerCase()} jobs.`,
    ctaLabel: "Get a Free SEO Audit",
    serviceFaqs: [
      {
        q: (industry) => `How long until my ${industry} business starts ranking?`,
        a: () =>
          `Map Pack movement typically begins in 30–90 days. Competitive organic keywords usually take 3–6 months of consistent on-page, content, and review work to crack page 1.`,
      },
      {
        q: (industry) => `Will SEO actually generate ${industry.toLowerCase()} leads, or just traffic?`,
        a: (industry, shortName) =>
          `We optimize for buyer-intent keywords — the searches that turn into phone calls, not just curious visitors. Every ${shortName} SEO engagement includes call tracking so you see exactly which keywords produce booked jobs.`,
      },
    ],
  },
  "web-design": {
    key: "web-design",
    slug: "web-design",
    serviceName: "Web Design",
    verb: "Build",
    hubPath: "/colorado-springs-web-design",
    pricingPath: "/pricing",
    icon: Globe,
    whatItIs:
      "Custom, mobile-first website design and build — fast-loading, conversion-focused sites that turn visitors into booked jobs (with a free mockup before you pay anything).",
    whyItMatters: (industry, shortName) =>
      `For a ${shortName}, your website is your hardest-working employee. A pretty site that doesn't ring the phone is just a digital business card — and the wrong impression costs real ${industry.toLowerCase()} jobs every week.`,
    pillars: [
      {
        title: "Industry-specific conversion design",
        body: (industry, shortName) =>
          `Layouts, CTAs, and trust signals tuned for the way ${industry.toLowerCase()} customers actually shop — emergency-ready, photo-rich, and built around the decisions a ${shortName} buyer makes.`,
      },
      {
        title: "Free custom mockup first",
        body: () =>
          `We design a real custom mockup of your new website before you pay a single penny. You only pay if you approve it. No deposits, no risk.`,
      },
      {
        title: "SEO + lead capture baked in",
        body: (industry) =>
          `Schema markup, fast Core Web Vitals, click-to-call, lead forms, and an SEO foundation tuned for ${industry.toLowerCase()} keywords — built in from day one.`,
      },
    ],
    meta: {
      title: (industry) => `${industry} Web Design in Colorado | Free Mockup | CWI`,
      description: (industry, shortName) =>
        `Custom websites for Colorado ${shortName} businesses. Mobile-first, conversion-built, with a free mockup before you pay. Most sites launch in 7–10 business days.`,
    },
    heroEyebrow: (industry) => `Web Design for ${industry}`,
    heroTitle: (industry) => ({
      lead: "Custom websites for Colorado ",
      gold: industry,
      trail: " companies — free mockup first",
    }),
    heroDescription: (industry, shortName) =>
      `Mobile-first, conversion-focused websites for Colorado ${shortName} businesses. We build a free custom mockup so you see your new ${industry.toLowerCase()} site before you pay a penny.`,
    ctaLabel: "Get My Free Mockup",
    serviceFaqs: [
      {
        q: (industry) => `How long does a ${industry} website take to build?`,
        a: () =>
          `Most sites launch within 7–10 business days after you approve the mockup. Larger multi-location or eCommerce builds take 3–6 weeks.`,
      },
      {
        q: (industry) => `Do I really get a free mockup before paying?`,
        a: (industry, shortName) =>
          `Yes — a real custom mockup of your ${shortName} website, built around your brand and services. You only pay if you approve and want to launch. Don't love it? You owe nothing.`,
      },
    ],
  },
  "google-maps": {
    key: "google-maps",
    // URL slug renamed 2026-04: /google-maps-for-hvac → /local-seo-for-hvac.
    // TS key stays "google-maps".
    slug: "local-seo",
    serviceName: "Local SEO (Google Maps)",
    verb: "Dominate",
    hubPath: "/colorado-springs-google-maps",
    pricingPath: "/pricing",
    icon: MapPin,
    whatItIs:
      "Google Business Profile optimization — the deep, ongoing work that puts you in the Google Map Pack (top 3) for the searches that actually book jobs.",
    whyItMatters: (industry, shortName) =>
      `For most local ${industry.toLowerCase()} searches, 60–70% of clicks go to the three businesses in the Google Map Pack. If your ${shortName} isn't in that 3-Pack, you're invisible to the bulk of your buyers — no matter how good your website is.`,
    pillars: [
      {
        title: "Review velocity that moves rankings",
        body: (industry, shortName) =>
          `Consistent, recent, keyword-rich reviews are the #1 ranking factor. We build review systems your ${shortName} can actually run — earning real reviews after every ${industry.toLowerCase()} job.`,
      },
      {
        title: "Active GBP posts + Q&A",
        body: () =>
          `Active profiles outrank dormant ones. We publish weekly posts, seed Q&A, and keep services, hours, and attributes optimized for your highest-intent searches.`,
      },
      {
        title: "Photos, categories & service areas",
        body: (industry, shortName) =>
          `Geo-tagged photos of your real ${shortName} work, paired with deep category and service-area optimization that signal local relevance to Google.`,
      },
    ],
    meta: {
      title: (industry) => `${industry} Google Maps SEO in Colorado | CWI`,
      description: (industry, shortName) =>
        `Get your Colorado ${shortName} into the Google Map Pack. Review velocity, GBP posts, and ongoing optimization that drives Top-3 rankings and booked ${industry.toLowerCase()} jobs.`,
    },
    heroEyebrow: (industry) => `Google Maps for ${industry}`,
    heroTitle: (industry) => ({
      lead: "Win the Google 3-Pack for Colorado ",
      gold: industry,
      trail: " searches",
    }),
    heroDescription: (industry, shortName) =>
      `Most Colorado customers never scroll past the Map Pack. We do the deep, ongoing GBP work that puts your ${shortName} in the top 3 — and keeps you there as competitors fight for position.`,
    ctaLabel: "Get a Free Map Pack Audit",
    serviceFaqs: [
      {
        q: (industry) => `How fast can my ${industry} business hit the Map Pack?`,
        a: () =>
          `Most clients see meaningful Map Pack movement in 30–90 days. Crowded urban categories take 4–6 months of consistent review velocity, posts, and citations to crack the top 3.`,
      },
      {
        q: () => `Do you actually post fake reviews?`,
        a: () =>
          `Never. That violates Google's terms and gets profiles suspended permanently. We build review-request systems that make it easy for your real customers to leave honest reviews after every job.`,
      },
    ],
  },
  ppc: {
    key: "ppc",
    slug: "ppc",
    serviceName: "Google Ads (PPC)",
    verb: "Convert",
    hubPath: "/colorado-springs-ppc",
    pricingPath: "/pricing",
    icon: MousePointerClick,
    whatItIs:
      "Google Ads management — high-intent paid search campaigns that put your business at the top of Google for the keywords that actually book jobs.",
    whyItMatters: (industry, shortName) =>
      `Organic SEO takes months. Google Ads can have your ${shortName} ringing the phone tomorrow — but only if campaigns are tuned for ${industry.toLowerCase()} buyer intent, not generic clicks. Untuned ads burn budget fast.`,
    pillars: [
      {
        title: "Buyer-intent keyword targeting",
        body: (industry, shortName) =>
          `We bid on the keywords that signal a buyer ready to book — not browsers. Negative-keyword discipline keeps your ${shortName} budget on real ${industry.toLowerCase()} prospects.`,
      },
      {
        title: "Service-area + dayparting controls",
        body: (industry) =>
          `Tight geo-targeting on the Colorado cities and ZIP codes you actually service, plus dayparting tuned to when ${industry.toLowerCase()} buyers convert (often after-hours and weekends).`,
      },
      {
        title: "Call tracking + landing pages",
        body: (industry, shortName) =>
          `Dedicated landing pages for each ${shortName} service, paired with call tracking on every campaign — so you see exactly which ad spend produces booked ${industry.toLowerCase()} jobs.`,
      },
    ],
    meta: {
      title: (industry) => `${industry} Google Ads in Colorado | PPC Management | CWI`,
      description: (industry, shortName) =>
        `Google Ads management for Colorado ${shortName} companies. Buyer-intent campaigns, call tracking, and tight geo-targeting that book real ${industry.toLowerCase()} jobs — not just clicks.`,
    },
    heroEyebrow: (industry) => `Google Ads for ${industry}`,
    heroTitle: (industry) => ({
      lead: "Google Ads that book real jobs for Colorado ",
      gold: industry,
      trail: " companies",
    }),
    heroDescription: (industry, shortName) =>
      `Pay-per-click campaigns tuned for Colorado ${shortName} buyer intent. Tight geo-targeting, real landing pages, and call tracking on every dollar — so you know what's working.`,
    ctaLabel: "Get a Free Google Ads Audit",
    serviceFaqs: [
      {
        q: (industry) => `What's a realistic Google Ads budget for ${industry.toLowerCase()}?`,
        a: () =>
          `Most Colorado service businesses start at $1,500–$3,000/month in ad spend, plus management. Budget scales with how many service areas, keywords, and seasons you want to compete in.`,
      },
      {
        q: (industry) => `How do I know Google Ads is actually working for my ${industry.toLowerCase()} business?`,
        a: () =>
          `Call tracking and conversion tracking on every campaign. You see cost per lead, cost per booked job, and which keywords drive real revenue — not just impressions and clicks.`,
      },
    ],
  },
};

export const INDUSTRY_SERVICE_KEYS = Object.keys(INDUSTRY_SERVICES) as IndustryServiceKey[];

/* ============================================================
   PRIORITY INDUSTRIES — full grid coverage
   These 10 industries get all 4 service pages built (40 pages total).
   Other industries fall back to the generic /industries/$slug hub.
============================================================ */
export const PRIORITY_INDUSTRY_SLUGS: IndustryKey[] = [
  "hvac",
  "plumbing",
  "roofing",
  "electrical",
  "landscaping",
  "general-contractors",
  "law-firms",
  "medical-dental",
  "restaurants-cafes",
  "auto-repair",
  // 2026-04 expansion — legal specialties
  "personal-injury-attorneys",
  "criminal-defense-attorneys",
  "family-law-attorneys",
  "estate-planning-attorneys",
  // medical specialties
  "dentists",
  "orthodontists",
  "physical-therapy",
  "med-spas",
  "plastic-surgeons",
  "dermatologists",
  "ophthalmologists",
  // commercial trades + heavy industry
  "commercial-roofing",
  "steel-buildings",
  "custom-home-builders",
  "trucking-hauling",
  "equipment-rental",
  "manufacturing",
  // B2B / financial
  "franchise-businesses",
  "mortgage-brokers",
];

/* ============================================================
   COMBO MODIFIERS — unique paragraph per (industry, service)
   When missing, fallbackComboModifier() generates a deterministic
   line so every page still has unique on-page text.
============================================================ */
const COMBO_MODIFIERS: Partial<
  Record<IndustryKey, Partial<Record<IndustryServiceKey, string>>>
> = {
  hvac: {
    seo: "HVAC SEO that targets the seasonal swings — furnace repair when the first cold front hits, AC install when summer breaks, tune-ups in shoulder months. We map your service pages to those exact buying cycles.",
    "web-design": "HVAC websites with sticky after-hours emergency CTAs, NATE/EPA trust signals up top, and financing badges that close the higher-ticket replacements.",
    "google-maps": "HVAC Map Pack work tuned to beat the franchises — disciplined review velocity right after each install, plus weekly GBP posts on tune-ups and seasonal promos.",
    ppc: "HVAC Google Ads built around emergency-call intent — tight dayparting on after-hours, dedicated landing pages for furnace vs. AC, and call tracking on every click.",
  },
  plumbing: {
    seo: "Plumbing SEO that wins emergency searches first — drain cleaning, water heater swaps, and burst-line keywords — then expands into higher-margin remodel work.",
    "web-design": "Plumbing websites where 'emergency 24/7' is unmistakable in the first 2 seconds, with click-to-call and live response time visible above the fold on mobile.",
    "google-maps": "Plumber Map Pack work that breaks the chain-franchise stranglehold — review velocity, geo-tagged truck and job photos, and active GBP posts on every service.",
    ppc: "Plumber Google Ads tuned for emergency intent — call-only campaigns after-hours, dedicated landers for drain vs. water heater vs. sewer line, with full call tracking.",
  },
  roofing: {
    seo: "Roofing SEO that captures storm-damage spikes — hail-damage and storm-restoration content built and ready when Colorado hail season hits.",
    "web-design": "Roofing websites with storm-damage landing pages, instant-quote forms, and credibility stack (insurance, GAF/Owens Corning) that closes the higher-ticket replacements.",
    "google-maps": "Roofer GBP work that ranks during storm windows when competitors scramble — review systems primed for post-install reviews and weekly storm-readiness posts.",
    ppc: "Roofing Google Ads built around storm-event intent — fast budget shifts when hail hits, dedicated storm-damage landers, and call tracking on every campaign.",
  },
  electrical: {
    seo: "Electrician SEO across the full service ladder — panel upgrades, EV charger installs, generator hookups, and emergency calls — each with its own ranking page.",
    "web-design": "Electrician websites with permit-confidence trust signals (licensed, bonded, insured) up top, plus dedicated landers for the higher-ticket panel and EV jobs.",
    "google-maps": "Electrician Map Pack work tuned to the categories that actually convert — service area, electrical contractor, and electrician — with active reviews on each.",
    ppc: "Electrician Google Ads built around the high-margin jobs (EV chargers, panel upgrades, generators), with negative keywords filtering out DIY browsers.",
  },
  landscaping: {
    seo: "Landscaping SEO tuned to Colorado's seasonal cycles — spring cleanup, summer install, fall leaf, and winter snow — each season with its own optimized pages.",
    "web-design": "Landscaping websites with gallery-rich design, before/after photos, and seasonal landing pages that capture the narrow Colorado install windows.",
    "google-maps": "Landscaping GBP work timed to spring rush — review velocity right when buyers are searching, plus weekly seasonal posts on installs and maintenance.",
    ppc: "Landscaping Google Ads with sharp seasonal budget shifts — heavy in March/April for design, summer for maintenance, fall for cleanup. Tight geo on actual service areas.",
  },
  "general-contractors": {
    seo: "Contractor SEO that targets remodel intent — kitchen, bath, basement, addition — each with its own page tuned for the longer research cycle GC buyers run.",
    "web-design": "GC websites built around portfolio depth — high-quality project galleries, real testimonials, and trust signals (license, insurance, NAHB) that win larger remodels.",
    "google-maps": "GC Map Pack work tuned for remodel categories where review quality matters more than volume — long-form reviews about completed projects beat one-liners.",
    ppc: "GC Google Ads built for remodel research — multi-touch campaigns, dedicated landers per remodel type, and longer attribution windows than typical service businesses.",
  },
  "law-firms": {
    seo: "Law firm SEO across practice-area depth — personal injury, family, criminal, estate — each practice gets its own deep, locally optimized landing page.",
    "web-design": "Law firm websites with attorney bios, case-result transparency, and trust-building design that converts skeptical, research-heavy buyers.",
    "google-maps": "Law firm GBP work tuned for the practice areas Google takes seriously — plus disciplined review systems that respect the bar's review rules.",
    ppc: "Legal Google Ads with extreme negative-keyword discipline — bid only on the buyer-intent keywords (cost-per-click is brutal otherwise), with conversion tracking per practice area.",
  },
  "medical-dental": {
    seo: "Medical and dental SEO tuned to insurance, procedure-specific, and emergency intent — with the YMYL credibility signals (provider bios, credentials) Google demands.",
    "web-design": "Medical/dental websites with HIPAA-aware forms, online booking integration, insurance-acceptance clarity, and provider trust signals up front.",
    "google-maps": "Medical/dental Map Pack work tuned for the procedure-specific searches your patients actually use — plus review systems that respect medical review platforms.",
    ppc: "Medical/dental Google Ads built around procedure intent (Invisalign, implants, etc.) and insurance keywords, with call-tracking attribution per service line.",
  },
  "restaurants-cafes": {
    seo: "Restaurant SEO tuned for menu, cuisine, and 'near me' searches — plus the Google Posts and photo work that drive the impulse-decision visit.",
    "web-design": "Restaurant websites with mobile-first menu PDFs that load fast, online ordering integration, and reservation/waitlist links that convert hungry mobile traffic.",
    "google-maps": "Restaurant GBP work tuned for the high-stakes review world of food — fresh photos, weekly posts on specials, and a review-response cadence that protects your rating.",
    ppc: "Restaurant Google Ads built for the high-conversion windows (Friday/Saturday evenings, weekend brunch) with location-extension and call-extension on every campaign.",
  },
  "auto-repair": {
    seo: "Auto repair SEO across the full service stack — brakes, transmission, oil change, diagnostics — each with its own page tuned for the make/model searches your customers run.",
    "web-design": "Auto repair websites with online booking, transparent service pricing, ASE/AAA trust signals, and click-to-call up front for the urgent-fix searches.",
    "google-maps": "Auto repair GBP work tuned for the categories with the best review-to-job conversion — plus systems for capturing reviews right at vehicle pickup.",
    ppc: "Auto repair Google Ads tuned for urgent service intent (brakes, check engine light, AC repair), with tight geo on real drive-time service areas.",
  },

  /* ====== 2026-04 EXPANSION COMBOS ====== */

  "personal-injury-attorneys": {
    seo: "Personal injury SEO that wins on practice-area depth — auto, motorcycle, truck, slip-and-fall, wrongful death — each with its own ranking page plus bilingual auto accident pages for Aurora, Westminster, and Pueblo.",
    "web-design": "PI websites with case-result transparency (bar-compliant), bilingual intake, and call-tracked forms wired to your case management system — built to convert serious injury searches, not tire-kicker forms.",
    "google-maps": "PI Map Pack work tuned for case-type searches — auto accident, motorcycle, truck — plus disciplined review velocity that respects Colorado bar rules on testimonials.",
    ppc: "PI Google Ads with extreme negative-keyword discipline — auto accident and serious injury intent only, tight geo on actual practice areas, and per-case-type call tracking (Denver CPCs hit $90+ without it).",
  },
  "criminal-defense-attorneys": {
    seo: "Criminal defense SEO across charge-specific pages — DUI, felony, domestic violence, federal — that capture urgent post-arrest searches mills can't compete on.",
    "web-design": "Criminal defense websites with sticky 'we answer jail calls 24/7' CTAs, charge-specific landing pages, and trial-result trust signals that beat the DUI-mill template.",
    "google-maps": "Criminal defense Map Pack work that escapes the DUI-mill cluster — disciplined post-resolution review systems plus weekly GBP posts on case wins (bar-compliant).",
    ppc: "Criminal defense Google Ads tuned for urgent intent — DUI arrest, domestic violence arrest, federal target letter — with after-hours dayparting and call-only campaigns.",
  },
  "family-law-attorneys": {
    seo: "Family law SEO that wins the long research cycle — contested divorce, mediation, custody, support, modifications, military divorce — each with its own deep ranking page.",
    "web-design": "Family law websites that read empathetic and competent (not law-firm-bunker) with research-cycle nurture flows that earn the consult after weeks of buyer research.",
    "google-maps": "Family law Map Pack work tuned for sensitive-search behavior — neighborhood-anchored content plus review systems that protect client privacy and build authority.",
    ppc: "Family law Google Ads with tight intent filters (consult-ready vs. researching), separate campaigns for divorce vs. custody vs. mediation, and lead scoring that respects the long sales cycle.",
  },
  "estate-planning-attorneys": {
    seo: "Estate planning SEO across plan-type depth — wills, revocable trusts, irrevocable, probate, special needs, asset protection, elder law — built for the two-buyer reality (young families + retirees).",
    "web-design": "Estate planning websites that read polished and high-trust — long-tenure cues, ACTEC/super-lawyer credentials, real attorney bios — built to convert HNW research-heavy buyers.",
    "google-maps": "Estate planning Map Pack work tuned for the high-trust buyer — disciplined long-form reviews, weekly educational GBP posts, and sub-specialty (probate, elder law) signal optimization.",
    ppc: "Estate planning Google Ads tuned for high-intent searches (probate, irrevocable trust, asset protection) with negative-keyword filtering on DIY and template-only browsers.",
  },
  dentists: {
    seo: "Dental SEO across procedure-specific pages — implants, Invisalign, veneers, crowns, emergency — each with its own ranking page plus insurance-acceptance and 'dentist near me' optimization.",
    "web-design": "Dental websites with online booking integration (NexHealth, LocalMed), HIPAA-aware intake, transparent insurance acceptance, and short-form new-patient flows that convert at 8%+.",
    "google-maps": "Dental Map Pack work that breaks the DSO stranglehold — review systems tuned for post-appointment capture, weekly GBP posts on procedure spotlights and new-patient specials.",
    ppc: "Dental Google Ads tuned for high-LTV procedure intent — implants, Invisalign, full-arch — with call tracking per procedure and negative keywords filtering DIY denture searches.",
  },
  orthodontists: {
    seo: "Orthodontic SEO with separate adult-Invisalign and teen-braces funnels — including educational content that beats SmileDirect/Candid on case-complexity transparency.",
    "web-design": "Orthodontic websites with adult vs. teen pathways, free-consult booking widgets, before/after galleries, and financing transparency that converts research-heavy buyers.",
    "google-maps": "Orthodontic Map Pack work tuned for adult Invisalign + family braces — review velocity at consult and case-complete points, plus GBP posts on smile reveals.",
    ppc: "Orthodontic Google Ads with separate Invisalign and braces campaigns, free-consult tracking per ad group, and negative keywords filtering DTC-aligner researchers.",
  },
  "physical-therapy": {
    seo: "PT SEO that capitalizes on Colorado direct-access — 'no referral needed' messaging plus specialty-deep pages for sports, post-surgical, dry needling, pelvic floor, and vestibular.",
    "web-design": "PT websites with above-the-fold direct-access positioning, specialty pathways, transparent cash-pay rates, HSA/FSA messaging, and online booking integration with WebPT or your EMR.",
    "google-maps": "PT Map Pack work tuned to neighborhood searches — review systems hitting after every discharge, plus GBP posts on patient outcomes (HIPAA-respectful) and specialty programs.",
    ppc: "PT Google Ads tuned for direct-access intent (no MD referral required), separate campaigns per specialty (sports, pelvic, post-op), with cash-pay and insurance ad groups.",
  },
  "med-spas": {
    seo: "Med spa SEO across treatment-specific pages — Botox, filler, laser hair removal, IPL, Morpheus8, CoolSculpting, EmSculpt — each with pricing transparency and provider credentials.",
    "web-design": "Med spa websites with treatment-specific landing pages, before/after galleries (FDA + Medical Board compliant), membership program flows, and financing partner integration.",
    "google-maps": "Med spa Map Pack work tuned for treatment-specific local searches — review systems at appointment-end, weekly GBP posts on injector spotlights, and seasonal package promotions.",
    ppc: "Med spa Google Ads tuned for high-LTV recurring treatments — Botox + filler retargeting, membership signup tracking, and per-treatment ROAS reporting.",
  },
  "plastic-surgeons": {
    seo: "Plastic surgery SEO with procedure-deep content — pre-op, recovery, surgical approach, risks — that wins the 6-month buyer research cycle premium buyers run.",
    "web-design": "Plastic surgery websites with board-certified credibility stack, properly consented before/after galleries, and consultation booking integration that converts qualified buyers.",
    "google-maps": "Plastic surgery Map Pack work tuned for procedure intent — RealSelf integration, post-op review velocity, and GBP posts that respect Medical Board cosmetic marketing rules.",
    ppc: "Plastic surgery Google Ads tuned for procedure research intent — separate campaigns for breast aug, mommy makeover, BBL, rhino — with long-attribution windows for the 6-month sales cycle.",
  },
  dermatologists: {
    seo: "Dermatology SEO across three funnels — medical (acne, eczema, psoriasis), surgical (Mohs, biopsies), cosmetic (Botox, lasers) — each with its own ranking page and intent path.",
    "web-design": "Dermatology websites with three-funnel architecture, sub-specialty credentials (Mohs fellowship, pediatric derm), online booking, and condition-specific intake flows.",
    "google-maps": "Dermatology Map Pack work tuned for both medical (skin check, acne) and cosmetic (Botox, lasers) searches — disciplined review systems for both buyer types.",
    ppc: "Dermatology Google Ads with separate medical, surgical, and cosmetic ad groups — call tracking per service line and negative keywords filtering DIY skin-care researchers.",
  },
  ophthalmologists: {
    seo: "Ophthalmology SEO across LASIK, PRK, SMILE, RLE, ICL, and cataract (with premium IOL) — built to win against LasikPlus, TLC, and the chain refractive competitors.",
    "web-design": "Ophthalmology websites with transparent LASIK pricing, premium IOL education for cataract upgrades, surgeon-specific credentials, and consultation booking flows.",
    "google-maps": "Ophthalmology Map Pack work tuned for surgical intent (LASIK, cataract) plus medical eye care (glaucoma, dry eye) — separate review systems for each buyer.",
    ppc: "Ophthalmology Google Ads with separate refractive (LASIK, RLE, ICL) and cataract (premium IOL) campaigns — pricing-transparent landing pages and per-procedure call tracking.",
  },
  "commercial-roofing": {
    seo: "Commercial roofing SEO across system-specific pages — TPO, EPDM, modified bitumen, standing seam metal, coatings — built for property managers, GCs, and facility directors (not residential).",
    "web-design": "Commercial roofing websites with B2B-buyer architecture — bonding capacity, references, certifications, RFP-ready bid forms — that property managers and GCs actually take seriously.",
    "google-maps": "Commercial roofing Map Pack work tuned for facility-director searches — categorized as commercial roofing contractor, with review velocity from completed projects (not residential reviews).",
    ppc: "Commercial roofing Google Ads tuned for B2B intent — TPO replacement, roof restoration, maintenance program — with separate campaigns for property management vs. GC vs. industrial buyers.",
  },
  "steel-buildings": {
    seo: "Steel building SEO across application-specific pages — ag, industrial, mini-storage, residential garage, aircraft hangar — with Colorado-code snow/wind load reference content national kit sellers can't write.",
    "web-design": "Steel building websites that surface local-erection advantages (permit help, foundation, turnkey) where national kit sellers can't compete — plus 3D configurator integration where applicable.",
    "google-maps": "Steel building Map Pack work tuned to county-level searches (Larimer, Weld, El Paso, Pueblo) — categorized as building contractor + supplier with completed-project review velocity.",
    ppc: "Steel building Google Ads tuned for application intent — ag building, mini-storage, garage/shop — with tight geo on actual delivery + erection radius.",
  },
  "custom-home-builders": {
    seo: "Custom home builder SEO with portfolio-depth case studies — not just gallery dumps — plus build-process transparency content that wins HNW buyers comparing 4-5 builders.",
    "web-design": "Custom home builder websites with photography-first design, completed-project case studies (budget range, timeline, owner narrative), and architect-collaboration positioning.",
    "google-maps": "Custom home builder Map Pack work tuned for luxury intent — review velocity from completed homes (not in-progress), GBP posts on completed projects with full architectural credit.",
    ppc: "Custom home builder Google Ads tuned for the long research cycle — long-attribution windows, retargeting for site visitors, separate campaigns for mountain vs. metro vs. semi-custom.",
  },
  "trucking-hauling": {
    seo: "Trucking SEO that breaks load-board dependence — direct shipper SEO with equipment-specific pages (dump truck, flatbed, reefer, hot shot, dirt + aggregate) and lane-specific content.",
    "web-design": "Trucking websites with carrier credentials (DOT/MC, insurance, equipment list) front-and-center, RFQ forms tuned to direct shippers, and SAFER/FMCSA reference integration.",
    "google-maps": "Trucking Map Pack work tuned for local construction + commercial hauling searches — categorized as trucking company + dump truck service with project-completion review velocity.",
    ppc: "Trucking Google Ads tuned for direct shipper acquisition — separate campaigns for construction hauling, ag, oil + gas service — with tight geo on actual lanes you run.",
  },
  "equipment-rental": {
    seo: "Equipment rental SEO across category-deep pages — skid steer, mini ex, scissor lift, boom lift, generator, dumpster, tool — with rate transparency and same-day availability messaging.",
    "web-design": "Equipment rental websites with real-time fleet availability, online quote + reservation flows, and contractor-friendly billing positioning that wins against United / Sunbelt on local relationship.",
    "google-maps": "Equipment rental Map Pack work tuned for category searches the chains underweight — neighborhood SEO + review systems at every rental return.",
    ppc: "Equipment rental Google Ads tuned for high-intent category searches with separate campaigns for contractor vs. DIY buyers — and rate-transparent landers that beat chain-call-for-rate landers.",
  },
  manufacturing: {
    seo: "Manufacturing SEO with capabilities-deep architecture — equipment list, material range, tolerance, finishing, certifications (AS9100, ISO, ITAR) — surfaced where engineers and procurement actually search.",
    "web-design": "Manufacturing websites with industry-vertical landing pages (aerospace, defense, medical, semi), short-form RFQ intake, ITAR-aware file upload, and CMMC/NIST 800-171 posture transparency.",
    "google-maps": "Manufacturing Map Pack work tuned for B2B sourcing intent — categorized properly (CNC machine shop, metal fabricator, contract manufacturer) with vertical-specific completed-project signals.",
    ppc: "Manufacturing Google Ads tuned for engineer + procurement intent — separate campaigns per industry vertical, RFQ tracking per ad group, and long-attribution windows for B2B sales cycles.",
  },
  "franchise-businesses": {
    seo: "Franchise SEO that wins local Map Pack and city-specific searches without violating brand standards — supplemental local site work that complements (not competes with) corporate.",
    "web-design": "Franchise websites built brand-compliant with corporate standards — adding the local SEO depth, neighborhood pages, and conversion architecture corporate doesn't build.",
    "google-maps": "Franchise Map Pack work tuned for your specific territory — neighborhood pages, local review systems, and GBP optimization that beats corporate-built location pages without territory conflict.",
    ppc: "Franchise Google Ads with location-specific call tracking, brand-compliant ad copy, and lead attribution separate from corporate spend so you see real local marketing ROI.",
  },
  "mortgage-brokers": {
    seo: "Mortgage SEO across loan-product-specific pages — purchase, refi, FHA, VA, jumbo, DSCR, HELOC — each tuned to that buyer's intent and rate sensitivity.",
    "web-design": "Mortgage websites with rate transparency, loan-product depth, and short-form pre-approval flows (5-7 fields) that convert at 6-10% — plus LO-specific routing and TRID/RESPA compliance.",
    "google-maps": "Mortgage Map Pack work tuned for purchase + refi searches — review systems hitting at funding, weekly GBP posts on rate updates and loan-program spotlights.",
    ppc: "Mortgage Google Ads with separate purchase, refi, FHA, VA, and jumbo campaigns — pre-approval conversion tracking and tight geo on your licensed states.",
  },
};

function fallbackComboModifier(
  industry: IndustryPage,
  service: IndustryServiceConfig,
): string {
  return `${service.serviceName} for Colorado ${industry.shortName}s — built around how ${industry.name.toLowerCase()} buyers actually search and convert in our market, with ${service.verb.toLowerCase()}-focused tactics tuned to your service mix.`;
}

/* ============================================================
   COMBO TYPE + LOOKUPS
============================================================ */
export type IndustryServiceCombo = {
  industry: IndustryPage;
  service: IndustryServiceConfig;
  /** "/seo-for-hvac" */
  path: string;
  /** absolute URL */
  url: string;
  /** unique on-page modifier paragraph */
  modifier: string;
};

const SITE_URL = "https://coloradowebimpressions.com";

export function buildIndustryServicePath(
  serviceSlug: string,
  industrySlug: string,
): string {
  return `/${serviceSlug}-for-${industrySlug}`;
}

export function buildIndustryServiceUrl(
  serviceSlug: string,
  industrySlug: string,
): string {
  return `${SITE_URL}${buildIndustryServicePath(serviceSlug, industrySlug)}`;
}

export function getIndustryServiceCombo(
  serviceSlug: string,
  industrySlug: string,
): IndustryServiceCombo | null {
  const service = Object.values(INDUSTRY_SERVICES).find((s) => s.slug === serviceSlug);
  if (!service) return null;
  if (!PRIORITY_INDUSTRY_SLUGS.includes(industrySlug as IndustryKey)) return null;
  const industry = INDUSTRY_PAGES.find((i) => i.slug === industrySlug);
  if (!industry) return null;

  const modifier =
    COMBO_MODIFIERS[industry.slug]?.[service.key] ??
    fallbackComboModifier(industry, service);

  return {
    industry,
    service,
    path: buildIndustryServicePath(service.slug, industry.slug),
    url: buildIndustryServiceUrl(service.slug, industry.slug),
    modifier,
  };
}

/** Every combo in the priority grid — used by sitemap + grid pages. */
export function getAllIndustryServiceCombos(): IndustryServiceCombo[] {
  const combos: IndustryServiceCombo[] = [];
  for (const service of Object.values(INDUSTRY_SERVICES)) {
    for (const industrySlug of PRIORITY_INDUSTRY_SLUGS) {
      const combo = getIndustryServiceCombo(service.slug, industrySlug);
      if (combo) combos.push(combo);
    }
  }
  return combos;
}

/** Other services for the same industry — used for in-page sibling links. */
export function getSiblingServiceCombosForIndustry(
  industrySlug: string,
  excludeServiceSlug: string,
): IndustryServiceCombo[] {
  return Object.values(INDUSTRY_SERVICES)
    .filter((s) => s.slug !== excludeServiceSlug)
    .map((s) => getIndustryServiceCombo(s.slug, industrySlug))
    .filter((x): x is IndustryServiceCombo => Boolean(x));
}

/** Other industries for the same service — used for in-page sibling links. */
export function getSiblingIndustryCombosForService(
  serviceSlug: string,
  excludeIndustrySlug: string,
): IndustryServiceCombo[] {
  return PRIORITY_INDUSTRY_SLUGS.filter((slug) => slug !== excludeIndustrySlug)
    .map((slug) => getIndustryServiceCombo(serviceSlug, slug))
    .filter((x): x is IndustryServiceCombo => Boolean(x));
}

/**
 * Anchor text variants for industry × service internal links on industry hubs.
 *
 * Same idea as the city-anchor rotator: keep the primary keyword
 * ({service} + {industry}) intact in every variant, but rotate phrasing so
 * a single industry hub doesn't list four near-identical anchors and so the
 * site doesn't surface the same anchor on every industry page. Indexed by
 * industry slug length + service idx for stability per page.
 */
const INDUSTRY_ANCHOR_PATTERNS: Record<
  IndustryServiceKey,
  ((industry: string) => string)[]
> = {
  seo: [
    (i: string) => `SEO for ${i}s`,
    (i: string) => `${i} SEO services`,
    (i: string) => `Search engine optimization for ${i} businesses`,
    (i: string) => `Local SEO built for ${i}s`,
  ],
  "web-design": [
    (i: string) => `Web design for ${i}s`,
    (i: string) => `${i} website design`,
    (i: string) => `Lead-gen websites for ${i} businesses`,
    (i: string) => `Custom websites built for ${i}s`,
  ],
  "google-maps": [
    (i: string) => `Local SEO for ${i}s`,
    (i: string) => `Google Maps optimization for ${i}s`,
    (i: string) => `${i} Google Business Profile help`,
    (i: string) => `Map Pack ranking for ${i} businesses`,
  ],
  ppc: [
    (i: string) => `Google Ads for ${i}s`,
    (i: string) => `${i} PPC management`,
    (i: string) => `Pay-per-click advertising for ${i}s`,
    (i: string) => `Lead-gen Google Ads for ${i} businesses`,
  ],
};

export function getIndustryAnchorVariant(
  serviceSlug: string,
  industryShortName: string,
  index: number,
): string {
  // Look up service config by URL slug, then use its TS key for pattern lookup.
  const svc = Object.values(INDUSTRY_SERVICES).find((s) => s.slug === serviceSlug);
  const patterns = svc ? INDUSTRY_ANCHOR_PATTERNS[svc.key] : undefined;
  if (!patterns || patterns.length === 0) {
    return `${svc?.serviceName ?? serviceSlug} for ${industryShortName}s`;
  }
  return patterns[index % patterns.length](industryShortName);
}
